import React, { ChangeEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Form, Table } from "react-bootstrap";

import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { getNestedProperty } from "../../services/UtilityService";
import DstButton from "../DstButton/DstButton";
import { DstTableColumns, DstTableProps } from "./DstTable.d";
import "./DstTable.scss";

const _prepareHeader = (input: Array<any>) => {
  let result: any[] = [];

  input.forEach((element: any) => {
    if (element.key) {
      result[0] = [...(result[0] ?? []), element];
    } else if (element.children) {
      result[0] = [
        ...(result[0] ?? []),
        {
          name: element.name,
          size: element.children.length,
        },
      ];
      result[1] = [...(result[1] ?? []), element.children].flat();
    }
  });
  return result;
};

export const DstTable = ({
  classLine,
  classTable,
  columns,
  data,
  itemsPerPage = 10,
  loading = false,
  onRowClick,
  onRowSelect,
  paginationResizeOptions = [],
  pagination = false,
  paginationAlwaysShowFooter = false,
  selectable = false,
  selectedItems = [],
  selectionKey = "id",
  stickyFirstColumnsCount = 0,
  striped = true,
  stylishLine,
}: DstTableProps) => {
  const { language } = useSession();

  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "ascending" | "descending";
    sortCompute?: Function;
  }>({
    key: null,
    direction: "ascending",
  });

  const [selectedRows, setSelectedRows] = useState<any[]>(selectedItems);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(itemsPerPage);
  const [showPageSizeSelect, setShowPageSizeSelect] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const [stickyOffsets, setStickyOffsets] = useState<number[]>([]);
  const [isHorizontallyScrolled, setIsHorizontallyScrolled] = useState(false);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

  const tableHeaders = useMemo(() => _prepareHeader(columns), [columns]);

  const dataColumnsCount = useMemo(
    () => tableHeaders.flat().filter((column: any) => !!column.key).length,
    [tableHeaders]
  );

  const effectiveStickyColumns = Math.min(Math.max(stickyFirstColumnsCount, 0), dataColumnsCount);

  useLayoutEffect(() => {
    if (!effectiveStickyColumns || !tableRef.current) {
      setStickyOffsets([]);
      setIsHorizontallyScrolled(false);
      setHasHorizontalOverflow(false);
      return;
    }

    const table = tableRef.current;
    const scrollContainer = table.closest(".table-responsive") as HTMLElement | null;

    const updateOverflowState = () => {
      const hasOverflow = !!scrollContainer && scrollContainer.scrollWidth > scrollContainer.clientWidth + 1;
      setHasHorizontalOverflow(hasOverflow);
      setIsHorizontallyScrolled(hasOverflow && scrollContainer.scrollLeft > 0);

      return hasOverflow;
    };

    const computeOffsets = () => {
      if (!updateOverflowState()) {
        setStickyOffsets([]);
        return;
      }

      const sourceRow =
        (table.querySelector("tbody tr") as HTMLTableRowElement | null) ??
        (table.querySelector("thead tr") as HTMLTableRowElement | null);

      if (!sourceRow) return;

      const cells = Array.from(sourceRow.querySelectorAll("th, td")) as HTMLElement[];
      const totalSticky = effectiveStickyColumns + (selectable ? 1 : 0);

      const offsets: number[] = [];
      let left = 0;

      for (let i = 0; i < Math.min(totalSticky, cells.length); i++) {
        offsets.push(left);
        left += cells[i].offsetWidth;
      }

      setStickyOffsets((previousOffsets) => {
        const isSame =
          previousOffsets.length === offsets.length &&
          previousOffsets.every((offset, index) => offset === offsets[index]);

        return isSame ? previousOffsets : offsets;
      });
    };

    computeOffsets();

    const resizeObserver = new ResizeObserver(() => {
      computeOffsets();
    });

    resizeObserver.observe(table);
    scrollContainer?.addEventListener("scroll", () => {
      updateOverflowState();
    });

    return () => {
      resizeObserver.disconnect();
      scrollContainer?.removeEventListener("scroll", () => {
        updateOverflowState();
      });
    };
  }, [effectiveStickyColumns, selectable, tableHeaders]);

  const getStickyStyle = (
    visualIdx: number,
    isHeader = false,
    backgroundColor?: React.CSSProperties["backgroundColor"]
  ): React.CSSProperties => {
    const totalSticky = (selectable ? 1 : 0) + effectiveStickyColumns;

    if (!hasHorizontalOverflow || !effectiveStickyColumns || visualIdx >= totalSticky || !stickyOffsets.length) {
      return {};
    }

    const isFirstSticky = visualIdx === 0;
    const isLastSticky = visualIdx === totalSticky - 1;

    const shadows: string[] = [];

    if (isHorizontallyScrolled) {
      shadows.push(
        "inset 0 0 0 9999px var(--bs-table-bg-state, var(--bs-table-bg-type, var(--bs-table-accent-bg, transparent)))"
      );

      if (isFirstSticky) {
        shadows.push("inset 1px 0 0 var(--bs-border-color)");
      }

      shadows.push("inset -1px 0 0 var(--bs-border-color)");
      shadows.push("inset 0 -1px 0 var(--bs-border-color)");

      if (isLastSticky) {
        shadows.push("2px 0 4px rgba(0, 0, 0, 0.08)");
      }
    }

    return {
      position: "sticky",
      left: stickyOffsets[visualIdx] ?? 0,
      zIndex: isHeader ? 3 : 2,
      backgroundColor: backgroundColor ?? "var(--bs-table-bg, #fff)",
      ...(shadows.length > 0 ? { boxShadow: shadows.join(", ") } : {}),
    };
  };

  const headerRow0VisualIndices = useMemo(() => {
    const indices: number[] = [];
    let visualIndex = selectable ? 1 : 0;

    (tableHeaders[0] || []).forEach((column: any) => {
      indices.push(visualIndex);
      visualIndex += column.size ?? 1;
    });

    return indices;
  }, [tableHeaders, selectable]);

  useEffect(() => {
    if (
      selectable &&
      selectedRows
        .map((elem) => elem[selectionKey])
        .sort()
        .toString() !==
        selectedItems
          .map((elem) => elem[selectionKey])
          .sort()
          .toString()
    ) {
      setSelectedRows(selectedItems);
    }
  }, [selectable, selectedRows, selectedItems, selectionKey]);

  useEffect(() => {
    if (showPageSizeSelect) {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setShowPageSizeSelect(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showPageSizeSelect]);

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, item: any) => {
    event.stopPropagation();
    const newSelectedRows = selectedRows.map((row) => row[selectionKey]).includes(item[selectionKey])
      ? selectedRows.filter((row) => row[selectionKey] !== item[selectionKey])
      : [...selectedRows, item];

    setSelectedRows(newSelectedRows);
    onRowSelect?.(newSelectedRows);
  };

  const handleSelectAllChange = () => {
    const elements = selectedRows.length === data.length ? [] : data;
    setSelectedRows(elements);
    onRowSelect?.(elements);
  };

  const requestSort = (column: DstTableColumns) => {
    setSortConfig((currentSortConfig) => ({
      key: column.key!,
      sortCompute: column?.sortCompute,
      direction:
        currentSortConfig.key === column.key && currentSortConfig.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const sortItems = (items: any[], key: string, direction: string) => {
    return items.sort((a, b) => {
      const aValue = getNestedProperty(a, key) || "";
      const bValue = getNestedProperty(b, key) || "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return direction === "ascending" ? aValue - bValue : bValue - aValue;
      }
      return direction === "ascending"
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });
  };

  const sortedItems = useMemo(() => {
    let tmpData = [...data];
    if (!!sortConfig.sortCompute && !!sortConfig.key) {
      tmpData = tmpData.map((elem) => ({
        ...elem,
        [sortConfig.key!]: sortConfig.sortCompute!(elem),
      }));
    }
    return sortConfig.key ? sortItems(tmpData, sortConfig.key, sortConfig.direction) : tmpData;
  }, [data, sortConfig]);

  const totalPages = useMemo(() => Math.ceil(sortedItems.length / pageSize), [sortedItems, pageSize]);

  const paginatedItems = useMemo(() => {
    if (!pagination) return sortedItems;
    const startIndex = (currentPage - 1) * pageSize;
    return sortedItems.slice(startIndex, startIndex + pageSize);
  }, [sortedItems, currentPage, pageSize, pagination]);

  const pageButtons = useMemo(() => {
    const maxButtons = 5;
    let startPage = Math.max(currentPage - Math.floor(maxButtons / 2), 1);
    const endPage = Math.min(startPage + maxButtons - 1, totalPages);

    if (endPage === totalPages) {
      startPage = Math.max(totalPages - maxButtons + 1, 1);
    }

    return Array.from({ length: Math.min(maxButtons, totalPages) }, (_, i) => startPage + i);
  }, [currentPage, totalPages]);

  return (
    <React.Fragment>
      <Table
        ref={tableRef}
        striped={striped}
        bordered
        responsive
        className={`s-regular dst-table ${classTable || ""}`}
        hover={!!onRowClick}
      >
        <thead>
          <tr>
            {selectable && (
              <th className="checkbox-cell text-center align-content-center" style={getStickyStyle(0, true)}>
                <Form.Check
                  type="checkbox"
                  checked={selectedRows.length === data.length && data.length > 0}
                  onChange={handleSelectAllChange}
                />
              </th>
            )}
            {tableHeaders.map((row: any, rowIndex: number) => (
              <React.Fragment key={`header-row-${rowIndex}`}>
                {row.map((column: any, colIndex: number) => (
                  <th
                    key={`header-col-${rowIndex}-${colIndex}`}
                    rowSpan={column.size ? 1 : 2}
                    colSpan={column?.size ?? 1}
                    onClick={() => requestSort(column)}
                    className="clickable align-content-center border-table"
                    style={{
                      ...(column.stylishTitle ? column.stylishTitle() : {}),
                      ...(rowIndex === 0 ? getStickyStyle(headerRow0VisualIndices[colIndex], true) : {}),
                    }}
                  >
                    {column.name}
                    {sortConfig.key === column.key && (
                      <span
                        className={`ms-2 ${
                          sortConfig.direction === "descending"
                            ? "dst-icon-chevron-down-double"
                            : "dst-icon-chevron-up-double"
                        }`}
                      />
                    )}
                  </th>
                ))}
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: pageSize }).map((_item, rowIndex) => (
                <tr key={`mock-row-${rowIndex}`}>
                  {selectable && (
                    <td className="text-center align-content-center" style={getStickyStyle(0)}>
                      <Form.Check disabled />
                    </td>
                  )}
                  {tableHeaders
                    .flat()
                    .filter((element: any) => !!element.key)
                    .map((element: any, colIndex: number) => (
                      <td
                        key={`mock-cell-${rowIndex}-${colIndex}`}
                        style={getStickyStyle(colIndex + (selectable ? 1 : 0))}
                      >
                        <div className="placeholder-glow">
                          <span className="placeholder col-6"></span>
                        </div>
                      </td>
                    ))}
                </tr>
              ))
            : paginatedItems.map((item, rowIndex) => (
                <tr
                  key={item[selectionKey] || `row-${rowIndex}`}
                  onClick={(event) => onRowClick?.(event, item)}
                  className={`${onRowClick ? "clickable" : ""} ${classLine?.(item) || ""}`}
                >
                  {selectable && (
                    <td
                      className="text-center align-content-center"
                      style={getStickyStyle(0)}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={selectedRows.map((row) => row[selectionKey]).includes(item[selectionKey])}
                        onChange={(event) => handleCheckboxChange(event, item)}
                      />
                    </td>
                  )}
                  {tableHeaders
                    .flat()
                    .filter((element: any) => !!element.key)
                    .map((element: any, cellIndex: number) => {
                      const lineStyle = stylishLine?.(item) ?? {};
                      const cellStyle = element?.stylishCell?.(item) ?? {};
                      const backgroundColor = cellStyle.backgroundColor ?? lineStyle.backgroundColor;

                      return (
                        <td
                          key={`data-${rowIndex}-${cellIndex}`}
                          style={{
                            ...lineStyle,
                            ...cellStyle,
                            ...getStickyStyle(cellIndex + (selectable ? 1 : 0), false, backgroundColor),
                          }}
                        >
                          {element.render ? element.render(item, rowIndex) : getNestedProperty(item, element.key)}
                        </td>
                      );
                    })}
                </tr>
              ))}
        </tbody>
      </Table>
      {!loading && (pagination || paginationAlwaysShowFooter) && totalPages > 1 && (
        <div className="pagination-controls d-flex justify-content-center align-items-center m-3">
          <DstButton
            btnClass="button-lister dst-icon-chevron-left-double me-1"
            clickFunction={() => setCurrentPage(1)}
            value=""
            disabled={currentPage === 1}
          />
          <DstButton
            btnClass="button-lister dst-icon-chevron-left me-1"
            clickFunction={() => setCurrentPage(Math.max(currentPage - 5, 1))}
            value=""
            disabled={currentPage < 4}
          />
          {pageButtons.map((page) => (
            <DstButton
              btnClass="button-lister ms-1"
              key={page}
              value={page.toString()}
              variant={currentPage === page ? "primary" : "secondary"}
              clickFunction={() => setCurrentPage(page)}
            />
          ))}
          <DstButton
            btnClass="button-lister dst-icon-chevron-right ms-2"
            value=""
            clickFunction={() => setCurrentPage(Math.min(currentPage + 5, totalPages))}
            disabled={currentPage > totalPages - 3}
          />
          <DstButton
            btnClass="button-lister dst-icon-chevron-right-double ms-1"
            clickFunction={() => setCurrentPage(totalPages)}
            value=""
            disabled={currentPage === totalPages}
          />
          {paginationResizeOptions.length > 0 && (
            <div className="ms-3 d-flex align-items-center position-relative">
              <span
                className="clickable d-flex align-items-center"
                onClick={() => setShowPageSizeSelect((showPageSizeSelect) => !showPageSizeSelect)}
              >
                <span className="ms-3 mb-0 s-regular">
                  {paginatedItems.length + (currentPage - 1) * pageSize} / {sortedItems.length}
                </span>
                <span className="dst-icon-settings-01 ms-2" />
              </span>
              {showPageSizeSelect && (
                <div
                  ref={dropdownRef}
                  className="position-absolute white-background border rounded shadow-sm text-center bottom-100 start-50 translate-middle-x"
                  style={{ minWidth: 150 }}
                >
                  {paginationResizeOptions.map((size) => (
                    <div
                      key={size}
                      className={`px-3 py-2 hover-bg-light clickable ${
                        pageSize === size ? "light-gray-background" : ""
                      }`}
                      onClick={() => {
                        setPageSize(size);
                        setShowPageSizeSelect(false);
                        setCurrentPage(1);
                      }}
                    >
                      {size} {translate(language, "COMPONENTS.DST_TABLE.RESIZABLE.PER_PAGE")}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {paginationResizeOptions.length === 0 && (
            <span className="ms-3">
              {paginatedItems.length + (currentPage - 1) * pageSize} / {sortedItems.length}
            </span>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default DstTable;
